import React from "react";
import { Tag } from "antd";
import { isValidElement } from "react";
import { fractureTypeOptions } from "../Options/systemicExamOptions";
import "./InspectionSummaries.css";
import moment from "moment";

/** Unwrap Form.List / API shapes like { remarks: "text" } or nested { remarks: { remarks: "x" } }. */
const unwrapRemarksValue = (v) => {
  if (v == null || typeof v !== "object") return v;
  if (Array.isArray(v)) return v;
  if (isValidElement(v)) return v;
  if (Object.prototype.hasOwnProperty.call(v, "remarks")) {
    return unwrapRemarksValue(v.remarks);
  }
  return v;
};
export const getLabelTagYesNO = (value) => {
  return value === "no" ? (
    <Tag bordered={false} className="break-line-tag">
      {"No"}
    </Tag>
  ) : null;
};

export const getLabel = (label) => {
  return (
    <Tag bordered={false} className="break-line-tag">
      {label}
    </Tag>
  );
};

/**
 * Free-text notes (diagnosis, remarks typed by the clinician) are shown exactly as
 * they were entered - line breaks and spacing are preserved instead of collapsed.
 */
export const getPreservedLabel = (label) => {
  return (
    <Tag bordered={false} className="break-line-tag preserve-entered-text">
      {label}
    </Tag>
  );
};

export const getPreservedLabelTagInput = (label, value) => {
  const renderValue = unwrapRemarksValue(value);
  return renderValue || renderValue === 0 ? (
    <Tag bordered={false} className="break-line-tag">
      <span className="summary-label">{label}: </span>
      <span className="summary-value preserve-entered-text">{renderValue}</span>
    </Tag>
  ) : null;
};

const normalizeSummaryValue = (value) => {
  if (value === null || value === undefined) return null;
  if (React.isValidElement(value)) return value;
  if (typeof value === "string" || typeof value === "number") return value;

  // Common "dynamic input" shape: { remarks: "..." }
  if (typeof value === "object") {
    if (Array.isArray(value)) {
      const parts = value
        .map((item) => {
          if (item === null || item === undefined) return null;
          if (typeof item === "string" || typeof item === "number") return item;
          if (typeof item === "object" && "remarks" in item) return item.remarks;
          return null;
        })
        .filter((v) => v !== null && v !== undefined && v !== "");
      return parts.length ? parts.join(", ") : null;
    }

    if ("remarks" in value) return value.remarks;
  }

  // Last resort: avoid crashing the UI
  return String(value);
};

export const getLabelTagInput = (label, value, after = null) => {
  const normalized = unwrapRemarksValue(value);
  const renderValue =
    normalized != null &&
    typeof normalized === "object" &&
    !Array.isArray(normalized) &&
    !isValidElement(normalized)
      ? JSON.stringify(normalized)
      : normalized;
  return renderValue || renderValue === 0 ? (
    <Tag bordered={false} className="break-line-tag">
      <span className="summary-label">{label}: </span>
      <span className="summary-value">{renderValue}</span>
      {after ? ` ${after}` : ""}
    </Tag>
  ) : null;
};

const formatString = (str) => {
  if (typeof str !== "string") {
    return str;
  }
  return str
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const getGussSummary = (data = {}) => {
  const tags = [];

  // Ensure 'data' is a valid object
  if (data && typeof data === "object") {
    for (const [key, value] of Object.entries(data)) {
      const formattedKey = formatString(key);

      if (value === null || value === undefined) {
        // Skip null or undefined values
        continue;
      } else if (typeof value === "string") {
        tags.push(getLabelTagInput(formattedKey, formatString(value)));
      } else if (typeof value === "object") {
        // Handle nested objects
        for (const [subKey, subValue] of Object.entries(value)) {
          const formattedSubKey = formatString(subKey);

          if (subValue != null) {
            tags.push(
              getLabelTagInput(`${formattedKey} - ${formattedSubKey}`, subValue)
            );
          }
        }
      }
    }
  }

  return tags;
};

export const getLabelTagTable = (array = []) => {
  return array.map((row, index) => {
    let tagContent = Object.entries(row)
      .filter(
        ([key, value]) =>
          key !== "key" &&
          key !== "action" &&
          key !== "other_sore" &&
          key !== "other_area"
      ) // ✅ hide other_sore as separate key
      .map(([key, value]) => {
        const formattedKey = formatString(key);

        // ✅ If area is "others", show the other_sore value instead
        const formattedValue =
          key === "area" &&
          value === "others" &&
          (row?.other_sore || row?.other_area)
            ? formatString(row.other_sore || row.other_area)
            : typeof value === "string"
            ? formatString(value)
            : value;

        return `${formattedKey}: ${formattedValue}`;
      })
      .join(", ");

    return (
      <Tag bordered={false} className="break-line-tag" key={index}>
        {tagContent}
      </Tag>
    );
  });
};

//Used for radio boxes
export const getLabelTag = (options = [], value = "") => {
  const label = options.find((option) => option.value === value)?.label || "";
  return label ? (
    <Tag bordered={false} key={value} className="break-line-tag">
      {label}
    </Tag>
  ) : null;
};
// Used for Multiselect checkboxes
export const getLabelTagsArray = (options = [], valuesArray = []) => {
  let raw = valuesArray;
  if (typeof raw === "string" && raw.includes(",")) {
    raw = raw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  const values = Array.isArray(raw)
    ? raw
    : raw != null && raw !== ""
    ? [raw]
    : [];
  const enteredOptions = options
    .filter(
      (option) => values.includes(option.value) && option.value !== "others"
    )
    .map((option) => option.label);

  return enteredOptions?.length === 0
    ? null
    : enteredOptions?.map((label, index) => (
        <Tag bordered={false} key={index} className="break-line-tag">
          {label}
        </Tag>
      ));
};


export const getLabelTextsArray = (options = [], valuesArray = []) => {
  let raw = valuesArray;
  if (typeof raw === "string" && raw.includes(",")) {
    raw = raw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  const values = Array.isArray(raw)
    ? raw
    : raw != null && raw !== ""
    ? [raw]
    : [];
  return options
    .filter(
      (option) => values.includes(option.value) && option.value !== "others"
    )
    .map((option) => option.label);
};

export const getDynamicInputTexts = (formData = []) => {
  if (!Array.isArray(formData)) return [];
  return formData
    .map((item) => unwrapRemarksValue(item?.remarks ?? item))
    .filter((value) => {
      if (value === null || value === undefined) return false;
      if (typeof value === "string") return value.trim() !== "";
      return true;
    })
    .map((value) => `${value}`.trim());
};

export const getLabelTagsWithChecklistLables = (
  options = [],
  formData = {},
  bordered = false,
  color
) => {
  const enteredOptions = options?.reduce((acc, option) => {
    if (formData?.[option?.value]) {
      acc?.push(option?.label);
    }
    return acc;
  }, []);
  return enteredOptions?.length === 0
    ? null
    : enteredOptions?.map((label, index) => (
        <Tag
          color={color}
          bordered={bordered}
          className="break-line-tag"
          key={index}
        >
          {label}
        </Tag>
      ));
};

//Used for CheckListFBYInput items
export const getLabelTagsWithDescription = (options = [], formData = {}) => {
  const enteredOptions = options.reduce((acc, option) => {
    if (formData?.[option.value]) {
      if (formData?.[option.value + "_description"]) {
        acc.push(
          `${option.label}: ${formData?.[option.value + "_description"]}`
        );
      } else {
        acc.push(option.label);
      }
    }
    return acc;
  }, []);

  return enteredOptions?.length === 0
    ? null
    : enteredOptions?.map((label, index) => (
        <Tag bordered={false} className="break-line-tag" key={index}>
          {label}
        </Tag>
      ));
};


export const getPastMedicalHistorySummary = (
  options = [],
  formData = {},
  { includeControl = true } = {}
) => {
  const enteredOptions = options.reduce((acc, option) => {
    let labelCreated = "";
    if (formData?.[option.value]) {
      labelCreated = option.label;

      if (option?.value === "past_medical_others") {
        const othersName = formData?.past_medical_others_name; // old format

        // ✅ New format — list
        if (Array.isArray(othersName) && othersName?.length) {
          const othersLabels = othersName
            ?.filter((item) => item?.name)
            ?.map((item) => {
              let label = item.name;
              if (item?.yrs) label += ` since ${item.yrs} yrs`;
              if (includeControl && item?.control)
                label += ` : ${formatString(item.control)}`;
              return label;
            })
            .join(", ");
          labelCreated = `${option.label}: ${othersLabels}`;

          // ✅ Old format — plain string
        } else if (typeof othersName === "string" && othersName) {
          labelCreated = `${option.label} name: ${othersName}`;
          if (formData?.past_medical_others_yrs)
            labelCreated += ` since ${formData.past_medical_others_yrs} yrs`;
          if (includeControl && formData?.past_medical_others_control)
            labelCreated += ` : ${formatString(
              formData.past_medical_others_control
            )}`;
        }
      } else {
        // Normal options — same as before
        if (formData?.[option.value + "_yrs"]) {
          labelCreated += ` since ${formData?.[option.value + "_yrs"]} yrs`;
        }
        if (includeControl && formData?.[option.value + "_control"]) {
          labelCreated += ` : ${formatString(
            formData?.[option.value + "_control"]
          )}`;
        }
      }
    }

    labelCreated && acc.push(labelCreated);
    return acc;
  }, []);

  return enteredOptions?.length === 0
    ? null
    : enteredOptions.map((label, index) => (
        <Tag bordered={false} className="break-line-tag" key={index}>
          {label}
        </Tag>
      ));
};

export const getEdemaSummary = (options = [], formData = {}) => {
  const enteredOptions = options.reduce((acc, option) => {
    let labelCreated = "";
    if (formData?.[option.value]) {
      labelCreated = option.label;
      if (formData?.[option.value + "_pitting"]) {
        labelCreated =
          labelCreated +
          ` Pitting:${formData?.[option.value + "_pitting_description"]}`;
      }
      if (formData?.[option.value + "_non_pitting"]) {
        labelCreated =
          labelCreated +
          ` Non Pitting:${
            formData?.[option.value + "_non_pitting_description"]
          }`;
      }
      if (formData?.[option.value + "_text"]) {
        labelCreated =
          labelCreated + ` : ${formData?.[option.value + "_text"]}`;
      }
    }
    labelCreated && acc.push(labelCreated);
    return acc;
  }, []);
  return enteredOptions?.length === 0
    ? null
    : enteredOptions?.map((label, index) => (
        <Tag bordered={false} className="break-line-tag" key={index}>
          {label}
        </Tag>
      ));
};

function getFractureType(value) {
  for (const group of fractureTypeOptions) {
    for (const option of group.options) {
      if (option.value === value) {
        return option.label;
      }
    }
  }
  return null;
}

export const getFractureSummary = (options = [], formData = {}) => {
  const enteredOptions = options.reduce((acc, option) => {
    let labelCreated = "";
    //  labelCreated = option.label;
    if (formData?.[option.value]) {
      labelCreated = option.label;
      if (formData?.[option.value + "_right_closed"]) {
        labelCreated = labelCreated + " Right-Closed";
        if (formData?.[option.value + "_right_closed_type"]) {
          labelCreated =
            labelCreated +
            ":" +
            getFractureType(formData?.[option.value + "_right_closed_type"]);
        }
      }
      if (formData?.[option.value + "_right_open"]) {
        labelCreated = labelCreated + " Right-Open";
        if (formData?.[option.value + "_right_open_type"]) {
          labelCreated =
            labelCreated +
            ":" +
            getFractureType(formData?.[option.value + "_right_open_type"]);
        }
      }
      if (formData?.[option.value + "_left_closed"]) {
        labelCreated = labelCreated + " Left-Closed";
        if (formData?.[option.value + "_left_closed_type"]) {
          labelCreated =
            labelCreated +
            ":" +
            getFractureType(formData?.[option.value + "_left_closed_type"]);
        }
      }
      if (formData?.[option.value + "_left_open"]) {
        labelCreated = labelCreated + " Left-Open";
        if (formData?.[option.value + "_left_open_type"]) {
          labelCreated =
            labelCreated +
            ":" +
            getFractureType(formData?.[option.value + "_left_open_type"]);
        }
      }
    }

    labelCreated && acc.push(labelCreated);
    return acc;
  }, []);
  return enteredOptions?.length === 0
    ? null
    : enteredOptions?.map((label, index) => (
        <Tag bordered={false} className="break-line-tag" key={index}>
          {label}
        </Tag>
      ));
};

// export const getTracheostomySummary = (formData = {}) => {
//   let labelCreated = "";
//   if (formData?.tracheostomy_type === "yes") {
//     labelCreated = formData?general_examination.tracheostomy_type;
//     if (formData?.["started_on"]) {
//       labelCreated =
//         labelCreated +
//         ` started ${dayjs(formData?.["number_of_days"]).format("YYYY-MM-DD")}`;
//     }
//     if (formData?.["secretion"]) {
//       labelCreated = labelCreated + ` : ${formData?.["secretion"]}`;
//     }
//   }

//   return <Tag key={"tracheostomy"}>{labelCreated}</Tag>;
// };

// Used for FormItems CheckListFBYSelect
export const getLabelTagsWithSelectItems = (
  checkOptions = [],
  selectOptions = [],
  formData = {}
) => {
  const enteredOptions = checkOptions.reduce((acc, option) => {
    if (formData?.[option.value]) {
      if (formData?.[option.value + "_type"]) {
        const selectedValue = formData?.[option.value + "_type"];
        const selectedOption = selectOptions.find(
          (option) => option.value === selectedValue
        );
        const selectedLabel = selectedOption
          ? selectedOption.label
          : selectedValue;
        acc.push(`${option.label}: ${selectedLabel}`);
      } else {
        acc.push(option.label);
      }
      if (formData?.[option?.value + "_type_others"]) {
        acc.push(
          `Others details: ${formData?.[option?.value + "_type_others"]}`
        );
      }
    }
    return acc;
  }, []);
  return enteredOptions?.length === 0
    ? null
    : enteredOptions?.length === 0
    ? null
    : enteredOptions?.map((label, index) => (
        <Tag bordered={false} className="break-line-tag" key={index}>
          {label}
        </Tag>
      ));
};
export const getExerciseSummary = (exerciseList) => {
  if (!exerciseList?.length) return null;

  return exerciseList.map((ex) => (
    <div key={ex.localId || ex.id} style={{ marginBottom: 8 }}>
      <strong>{ex.name}</strong>
      {getLabelTagInput("Exercise Type", ex.exercise_type || "-")}
      {getLabelTagInput(
        "Frequency",
        ex.frequency !== undefined && ex.frequency !== null ? ex.frequency : "-"
      )}

      {getLabelTagInput(
        "Intensity",
        ex.intensity !== undefined && ex.intensity !== null ? ex.intensity : "-"
      )}
      {getLabelTagInput("No.of Days", ex.duration || "-")}
      {getLabelTagInput(
        "Start Date",
        ex.startDate ? moment(ex.startDate).format("YYYY-MM-DD") : "-"
      )}
      {ex.source_type && getLabelTagInput("Source", ex.source_type)}
    </div>
  ));
};

// This block to be used when a list of checkboxes is to be displayed with radio options for each checkbox
// The radio options are displayed only when the checkbox is checked
// Here is the sample data object to be used.
//  const jointPainLocationOptions = [
//   { label: "Elbow", value: "elbow" },
//   { label: "Wrist", value: "wrist" },
//   { label: "Shoulder", value: "shoulder" },
//   { label: "Hip", value: "hip" },
//   { label: "Knee", value: "knee" },
//   { label: "Ankle", value: "ankle" },
//   { label: "Small joints of hand", value: "small_joints_of_hand" },
//   { label: "Small Joints of Foot", value: "small_joints_of_foot" },
//   { label: "Others", value: "others" },
// ];

//  const jointPainSeverityOptions = [
//   {
//     label: "Left",
//     name_key: "left",
//     options: [
//       { label: "Tenderness", value: "tenderness" },
//       { label: "Swelling", value: "swelling" },
//       { label: "Decreased ROM", value: "decreased_rom" },
//     ],
//   },
//   {
//     label: "Right",
//     name_key: "right",
//     options: [
//       { label: "Tenderness", value: "tenderness" },
//       { label: "Swelling", value: "swelling" },
//       { label: "Decreased ROM", value: "decreased_rom" },
//     ],
//   },
// ];

//Used for CheckListFBYCheckGroup items

export const getLabelTagsWithCheckGroup = (
  checkBoxOptions = [],
  checkBoxGroupOptions = [],
  formData = {}
) => {
  const enteredOptions = checkBoxOptions.reduce((acc, option) => {
    if (formData?.[option.value]) {
      const groupDetails = checkBoxGroupOptions.reduce(
        (details, checkBoxGroupOption) => {
          const selectedValues =
            formData?.[`${option.value}_${checkBoxGroupOption.name_key}`];
          if (Array.isArray(selectedValues) && selectedValues.length > 0) {
            const selectedLabels = selectedValues.map((selectedValue) => {
              const selectedOption = checkBoxGroupOption.options.find(
                (opt) => opt.value === selectedValue
              );
              return selectedOption ? selectedOption.label : selectedValue;
            });
            details.push(
              `${checkBoxGroupOption.label} => ${selectedLabels.join(", ")}`
            );
          }
          return details;
        },
        []
      );

      if (groupDetails.length > 0) {
        acc.push(`${option.label}: ${groupDetails.join(" : ")}`);
      } else {
        acc.push(option.label);
      }
    }
    return acc;
  }, []);

  return enteredOptions?.length === 0
    ? null
    : enteredOptions?.map((label, index) => (
        <Tag bordered={false} className="break-line-tag" key={index}>
          {label}
        </Tag>
      ));
};

// Used for CheckListFBYRadioItems. Remember the formitem name should be the same as the name_key in the radioOptions
export const getLabelTagsWithRadioOptions = (
  checkBoxOptions = [],
  radioOptions = [],
  formData = {}
) => {
  const enteredOptions = checkBoxOptions.reduce((acc, option) => {
    if (formData?.[option?.value]) {
      const groupLabels = radioOptions.reduce((labels, radioOptionGroup) => {
        if (formData?.[`${option.value}_${radioOptionGroup.name_key}`]) {
          const selectedValue =
            formData?.[`${option.value}_${radioOptionGroup.name_key}`];
          const selectedOption = radioOptionGroup?.options?.find(
            (opt) => opt.value === selectedValue
          );

          if (selectedOption) {
            labels.push(selectedOption.label);
          }
        }
        return labels;
      }, []);
      const displayLabel =
        option.value === "others" && formData?.others_name
          ? formData.others_name
          : option.label;
      if (groupLabels.length > 0) {
        acc.push(`${displayLabel}: ${groupLabels.join(", ")}`);
      } else {
        acc.push(displayLabel);
      }
    }
    return acc;
  }, []);

  return enteredOptions?.length === 0
    ? null
    : enteredOptions?.map((label, index) => (
        <Tag bordered={false} className="break-line-tag" key={index}>
          {label}
        </Tag>
      ));
};

// <Tag color="magenta">magenta</Tag>
//       <Tag color="red">red</Tag>
//       <Tag color="volcano">volcano</Tag>
//       <Tag color="orange">orange</Tag>
//       <Tag color="gold">gold</Tag>
//       <Tag color="lime">lime</Tag>
//       <Tag color="green">green</Tag>
//       <Tag color="cyan">cyan</Tag>
//       <Tag color="blue">blue</Tag>
//       <Tag color="geekblue">geekblue</Tag>
//       <Tag className="break-line-tag">purple</Tag>

export const getLabelScalesTags = (options = [], values = {}) => {
  return options
    .filter((option) => values[option.value] !== undefined)
    .map((option, index) => (
      <Tag bordered={false} className="break-line-tag" key={index}>
        {option.label}: {formatString(values[option.value])}
      </Tag>
    ));
};

export const getDepressionScaleTags = (options = [], values = {}) => {
  const getValue = (value) => {
    if (typeof value !== "string") return value;
    return value.startsWith("1") ? 1 : value.startsWith("2") ? 2 : 3;
  };

  return options
    .filter((option) => values[option.value] !== undefined)
    .map((option, index) => {
      const value = getValue(values[option.value]);
      return (
        <Tag bordered={false} key={index} className="break-line-tag">
          {option.label}: {value}
        </Tag>
      );
    });
};

export const getTherapyLabelTags = (therapyOptions, formData = {}) => {
  const selectedOptions = therapyOptions.reduce((acc, therapy) => {
    const selectedOption = formData[therapy.name_key];
    if (selectedOption) {
      const selectedLabel = therapy.options.find(
        (option) => option.value === selectedOption
      )?.label;
      if (selectedLabel) {
        acc.push(`${therapy.label}: ${selectedLabel}`);
      }
    }
    return acc;
  }, []);
  return selectedOptions?.length === 0
    ? null
    : selectedOptions?.map((label, index) => (
        <Tag bordered={false} key={index} className="break-line-tag">
          {label}
        </Tag>
      ));
};

export const getPatientConditionTags = (
  speechOptions = [],
  yesNoOptions = [],
  formData = {}
) => {
  const enteredOptions = speechOptions.reduce((acc, option) => {
    if (formData?.general_examination?.[option.value]) {
      const status = formData?.general_examination?.[`${option.value}_status`];
      const comments =
        formData?.general_examination?.[`${option.value}_comments`];

      const statusLabel =
        yesNoOptions.find((opt) => opt.value === status)?.label || " ";

      const label = `${option.label}: ${statusLabel}${
        comments ? `, Comments: ${comments}` : ""
      }`;

      acc.push(label);
    }
    return acc;
  }, []);
  return enteredOptions.length === 0
    ? null
    : enteredOptions.map((label, index) => (
        <Tag bordered={false} key={index} className="break-line-tag">
          {label}
        </Tag>
      ));
};

export const getLabelTagswithCheckbox = (
  checkbox = [],
  key = null,
  formData = {}
) => {
  const enteredOptions = checkbox?.reduce((acc, cur) => {
    if (formData?.[`${key}_${cur.value}`]) {
      acc.push(cur?.label);
    }
    return acc;
  }, []);
  return enteredOptions.length === 0
    ? null
    : enteredOptions.map((label, index) => (
        <Tag bordered={false} key={index} className="break-line-tag">
          {label}
        </Tag>
      ));
};

export const getLabelTagsWithDynamicOthers = (formData = {}) => {
  const enteredOptions = Object?.entries(formData || {})?.reduce((acc, cur) => {
    const raw = unwrapRemarksValue(cur[1]);
    if (raw === null || raw === undefined || raw === "") return acc;
    acc.push({ label: cur[0], value: raw });
    return acc;
  }, []);

  return enteredOptions?.length === 0
    ? null
    : enteredOptions?.map(({ label, value }, index) => (
        <Tag bordered={false} key={index} className="break-line-tag">
          {`${formatString(label)}: ${value}`}
        </Tag>
      ));
};

export const getLabelTagsWithDynamicInputs = (formData = []) => {
  const enteredOptions = Array.isArray(formData)
    ? formData
        .map((item) => unwrapRemarksValue(item?.remarks ?? item))
        .filter((value) => {
          if (value === null || value === undefined) return false;
          if (typeof value === "string") return value.trim() !== "";
          return true;
        })
    : [];

  return enteredOptions?.length === 0
    ? null
    : enteredOptions?.map((value, index) => (
        <Tag bordered={false} key={index} className="break-line-tag">
          {`${value}`}
        </Tag>
      ));
};

/** True when dynamic others list has at least one non-empty remark. */
export const hasDynamicOthersContent = (formData = []) => {
  if (!Array.isArray(formData) || formData.length === 0) return false;
  return formData.some((item) => {
    const value = unwrapRemarksValue(item?.remarks ?? item);
    if (value === null || value === undefined) return false;
    if (typeof value === "string") return value.trim() !== "";
    return true;
  });
};
